/**
 * Utility functions for handling viewer operations
 * Shared between DirectoryButtonBar and AssetRelationShipsButtonBar
 */

import { checkViewerSupport } from '../api/viewerSupportService';

/**
 * Opens a viewer and loads a document
 * @param {Function} openViewerFunction - Function to open the viewer (from ref)
 * @param {Function} setSelectedUrn - Function to set the URN for the viewer
 * @param {string} urn - The URN of the document to load
 * @param {string} viewerName - Name of the viewer for logging (e.g., 'left', 'right')
 */
export const openViewerWithDocument = async (openViewerFunction, setSelectedUrn, urn, viewerName = 'viewer') => {
    console.log(`=== openViewerWithDocument ${viewerName} DEBUG ===`);
    console.log(`URN to load:`, urn);
    
    if (!urn) {
        console.log(`Early return - no URN provided for ${viewerName}`);
        return;
    }
    
    // Open the viewer if it's not already opened
    if (openViewerFunction) {
        console.log(`Opening ${viewerName} viewer...`);
        openViewerFunction();
        
        // Add a small delay to allow the viewer to initialize before setting the URN
        await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    try {
        console.log(`Setting ${viewerName} viewer URN:`, urn);
        setSelectedUrn(urn);
    } catch (error) {
        console.error(`Error setting ${viewerName} viewer URN:`, error);
    }
    
    console.log(`=== END openViewerWithDocument ${viewerName} DEBUG ===`);
};

/**
 * Extracts URN from DirectoryButtonBar node data
 * @param {Object} nodeObj - The selected node object from directory tree
 * @returns {string|null} - The extracted URN or null if not found
 */
export const extractUrnFromDirectoryNode = (nodeObj) => {
    console.log('=== extractUrnFromDirectoryNode DEBUG ===');
    console.log('Node object:', nodeObj);
    
    if (!nodeObj) {
        console.log('No node object provided');
        return null;
    }
    
    // Handle version nodes - use storage directly
    if (nodeObj.isVersion || nodeObj.type === 'version') {
        console.log('=== VERSION NODE HANDLING ===');
        console.log('Version node object:', nodeObj);
        console.log('Raw storage from version node:', nodeObj.storage);
        console.log('Version data:', nodeObj.versionData);
        
        // The perfect URN is already in versionData.id!
        const perfectUrn = nodeObj.versionData?.id;
        
        if (perfectUrn) {
            console.log('Using perfect URN from versionData.id:', perfectUrn);
            return perfectUrn;
        } else {
            console.error('No versionData.id found in version node');
            console.log('Available versionData:', nodeObj.versionData);
            console.log('Falling back to storage:', nodeObj.storage);
            
            // Fallback to storage if versionData.id is not available
            if (nodeObj.storage) {
                return nodeObj.storage;
            }
        }
    } else {
        // Handle regular file nodes
        console.log('=== REGULAR FILE NODE HANDLING ===');
        console.log('File node object:', nodeObj);
        console.log('Raw storage from file node:', nodeObj.storage);
        
        if (nodeObj.storage) {
            return nodeObj.storage;
        }
    }
    
    console.log('No URN found in node object');
    return null;
};

/**
 * Extracts URN from AssetRelationShipsTable row data
 * @param {Object} rowData - The selected row data from the table
 * @returns {string|null} - The extracted URN or null if not found
 */
export const extractUrnFromTableRow = (rowData) => {
    console.log('=== extractUrnFromTableRow DEBUG ===');
    console.log('Row data:', rowData);
    
    if (!rowData) {
        console.log('No row data provided');
        return null;
    }
    
    const urn = rowData.itemUrn;
    console.log('Extracted URN from row:', urn);
    
    return urn || null;
};

/**
 * Checks if a directory node represents a file that can be opened in viewer
 * @param {Object} nodeObj - The node object from directory tree
 * @returns {boolean} - True if the node is a file that can be opened
 */
export const isFileNode = (nodeObj) => {
    if (!nodeObj) return false;
    
    // Check for version nodes - these are always selectable for viewer
    if (nodeObj.isVersion || nodeObj.type === 'version') {
        return true;
    }
    
    // Check for file extension in name
    const hasExtension = nodeObj.name && nodeObj.name.includes('.');
    
    // Check for file type indicators
    const isFile = nodeObj.type === 'file' ||
                  nodeObj.type === 'items' ||
                  nodeObj.objectType === 'file' ||
                  nodeObj.isFile ||
                  hasExtension;
                  
    const isNotFolder = nodeObj.type !== 'folder' &&
                       nodeObj.type !== 'folders' &&
                       nodeObj.objectType !== 'folder';
    
    return isFile && isNotFolder;
};

/**
 * Checks if table row data represents a document that can be opened in viewer
 * @param {Object} rowData - The row data from the table
 * @returns {boolean} - True if the row represents a document that can be opened
 */
export const isDocumentRow = (rowData) => {
    if (!rowData) return false;
    
    // Check if it has itemUrn (indicates it's a document)
    return !!(rowData.itemUrn);
};

/**
 * Checks if a document is supported by the Autodesk Viewer
 * @param {string} urn - The document URN to check
 * @param {string} accessToken - The access token for authentication
 * @returns {Promise<boolean>} - True if the document is supported by the viewer
 */
export const isDocumentViewerSupported = async (urn, accessToken) => {
    if (!urn || !accessToken) {
        return false;
    }
    
    try {
        return await checkViewerSupport(urn, accessToken);
    } catch (error) {
        console.error('Error checking document viewer support:', error);
        return false;
    }
};

/**
 * Enhanced check that combines document existence and viewer support
 * @param {Object} rowData - The row data from the table
 * @param {string} accessToken - The access token for authentication
 * @returns {Promise<boolean>} - True if the document exists and is viewer-supported
 */
export const isDocumentViewable = async (rowData, accessToken) => {
    // First check if it's a valid document
    if (!isDocumentRow(rowData)) {
        return false;
    }
    
    // Then check if it's supported by the viewer
    const urn = extractUrnFromTableRow(rowData);
    return await isDocumentViewerSupported(urn, accessToken);
};

/**
 * Enhanced check for directory nodes with viewer support
 * @param {Object} nodeObj - The node object from directory tree
 * @param {string} accessToken - The access token for authentication
 * @returns {Promise<boolean>} - True if the file exists and is viewer-supported
 */
export const isFileViewable = async (nodeObj, accessToken) => {
    // First check if it's a valid file
    if (!isFileNode(nodeObj)) {
        return false;
    }
    
    // Then check if it's supported by the viewer
    const urn = extractUrnFromDirectoryNode(nodeObj);
    return await isDocumentViewerSupported(urn, accessToken);
};